import React, { createContext, useContext, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { User } from "@supabase/supabase-js";
import { Database } from "@/shared/types/database.types";
import { identifyUser } from "@/shared/utils/core";
import { sentryUtils } from "@/shared/utils/sentry";
import { normalizeNameFields } from "@/shared/utils/names";

type Tables = Database["public"]["Tables"];
type UserRow = Tables["users"]["Row"];
type EstateRow = Tables["estates"]["Row"];

export interface AppContextData {
  authUser: User | null;
  user: UserRow | null;
  person: EstateRow | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const AppContext = createContext<AppContextData | undefined>(undefined);

export function AppContextProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        queryClient.invalidateQueries();
      }

      if (event === "SIGNED_IN") {
        queryClient.invalidateQueries({ queryKey: ["auth", "user"] });
        queryClient.invalidateQueries({ queryKey: ["auth-user"] });
      }

      if (event === "TOKEN_REFRESHED") {
        queryClient.invalidateQueries({ queryKey: ["auth", "user"] });
        queryClient.invalidateQueries({ queryKey: ["auth-user"] });
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const { data: authData, isLoading: authLoading } = useQuery({
    queryKey: ["auth-user"],
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      return user;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const authUser = authData || null;

  const {
    data: userData,
    isLoading: userLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["app-user-data", authUser?.id],
    queryFn: async () => {
      if (!authUser?.id) {
        return { user: null, person: null };
      }
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 10000);

      try {
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("*")
          .eq("auth_id", authUser.id)
          .abortSignal(controller.signal)
          .single();

        if (userError) {
          if (userError.code === "PGRST116") {
            return { user: null, person: null };
          }
          throw userError;
        }

        let personData = null;

        if (userData?.user_id) {
          const { data: userEstates, error: userEstatesError } = await supabase
            .from("user_estates")
            .select("estate_id")
            .eq("user_id", userData.user_id)
            .abortSignal(controller.signal);

          if (!userEstatesError && userEstates && userEstates.length > 0) {
            let targetPersonId = userData.last_active_estate_id;

            if (!targetPersonId || !userEstates.some((up) => up.estate_id === targetPersonId)) {
              targetPersonId = userEstates[0].estate_id;
            }

            const { data: personResult } = await supabase
              .from("estates")
              .select("*")
              .eq("estate_id", targetPersonId)
              .abortSignal(controller.signal)
              .single();

            personData = personResult;
          }
        }

        clearTimeout(timeoutId);
        return {
          user: userData ? normalizeNameFields(userData) : userData,
          person: personData ? normalizeNameFields(personData) : personData,
        };
      } catch (error: unknown) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === "AbortError") {
          throw new Error("Database query timed out. Please refresh the page.");
        }
        throw error;
      }
    },
    enabled: !!authUser?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
  });

  useEffect(() => {
    if (authUser && userData?.user) {
      identifyUser({
        authUserId: authUser.id,
        firstName: userData.user.first_name || undefined,
        surName: userData.user.last_name || undefined,
        email: authUser.email || undefined,
        preferredName: userData.user.preferred_name || undefined,
        isPrimaryAccountHolder: true,
      }).catch((error) => console.error("Failed to identify user:", error));
    }
  }, [authUser, userData?.user]);

  useEffect(() => {
    if (!authUser) {
      sentryUtils.clearUserContext();
      return;
    }
    if (userData?.user) {
      const estateId =
        userData.person?.estate_id != null ? String(userData.person.estate_id) : undefined;
      sentryUtils.setUserContext(
        String(userData.user.user_id),
        authUser.email ?? undefined,
        estateId,
      );
    }
  }, [authUser, userData?.user, userData?.person?.estate_id]);

  const value: AppContextData = {
    authUser,
    user: userData?.user || null,
    person: userData?.person || null,
    isLoading: authLoading || (!!authUser && userLoading),
    error: error as Error | null,
    refetch: () => {
      refetch();
    },
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within AppContextProvider");
  }
  return context;
}

export function useAuthUser() {
  const { authUser } = useAppContext();
  return authUser;
}

export function useUser() {
  const { user } = useAppContext();
  return user;
}

export function usePerson() {
  const { person } = useAppContext();
  return person;
}
